import type { FlightPlannerCopy } from './planner_copy.js';
import type { PlannerHeatRequest, PlannerHeatView } from './heat_view.js';
import type {
  PlannerLeg,
  PlannerRecentAirports,
  PlannerSubmit,
  PlannerTraveler,
  PlannerTravelerDirectory,
  PlannerTripType,
} from './planner_models.js';
import type { SearchSelectOption, SearchSelectSection } from './place_option.js';
import type { PlannerGuide, useGuidedFill } from './guided_fill.js';
import type { SkirtBest } from './heat_savings.js';
import type { PlannerState } from './planner_state.js';
import type { BinaryPlanner } from './binary_planner.js';
import type { MultiPlanner } from './multi_planner.js';

/*
 * Os tipos do `PlannerForm`: os valores iniciais, as props públicas, o eco do
 * submit e as props internas do corpo. Saíram de `planner_form.tsx` pelo teto
 * de linhas dele, junto com `recentSection`, que as props do corpo tipam.
 */

/** Valores INICIAIS do formulário — retrato do mount (mesma semântica dos seeds
 *  `useState(ui.*)` que o card sempre teve): edições posteriores vivem no estado
 *  interno do form. O card passa o próprio bloco (shape estruturalmente contido). */
export type PlannerFormValues = {
  route: {
    origin: string;
    destination: string;
    originLabel?: string;
    destinationLabel?: string;
  };
  departDate?: string;
  returnDate?: string;
  tripType?: PlannerTripType;
  legs?: PlannerLeg[];
  passengers?: number;
  travelers?: PlannerTraveler[];
  policyTravelerId?: string;
  requesterId?: string;
};

/**
 * A seção de sugestão de UMA ponta, ou `undefined` quando não há o que sugerir.
 *
 * `undefined` e não `[]` porque é o que o combobox lê como "sem sugestão" (e volta a
 * pedir "Digite para buscar"), e porque `exactOptionalPropertyTypes` não deixa passar
 * a prop com valor ausente — daí o spread condicional em quem a repassa.
 *
 * **Uma seção só, e ela é "Recentes".** O fio (`GET /flight/airports/history`) traz
 * DOIS conjuntos — origem e destino —, e ambos são histórico DO VIAJANTE: é a mesma
 * espécie de dado, separada por ponta da rota, não um segundo critério. Um bloco
 * "Mais buscados" exigiria uma contagem de popularidade que nenhum endpoint devolve.
 */
export function recentSection(
  recent: PlannerRecentAirports | undefined,
  endpoint: keyof PlannerRecentAirports,
  title: string,
): SearchSelectSection[] | undefined {
  const options = recent?.[endpoint] ?? [];
  return options.length > 0 ? [{ title, options }] : undefined;
}

export type PlannerFormProps = {
  initial: PlannerFormValues;
  heat: PlannerHeatView;
  /** Heat em voo: a grade vira shimmer (chat: `busy` do shell; bancada: fetch local). */
  heatLoading?: boolean;
  readOnly?: boolean;
  /** Nota do rodapé: no layout aberto ela é a `CardFooter` do card — hoje só os
   *  dois overlays da pill a montam (#2129); o card do chat não tem rodapé
   *  (#2207) e não a passa. No colapsado ela vai pro rodapé do popover, junto
   *  do CTA que ela qualifica. */
  note?: string;
  /** Rótulo do CTA; default `t('flightPlanner.search')` (colapsado:
   *  `t('flightPlanner.confirmDates')`). */
  submitLabel?: string;
  /**
   * **Quanta superfície o form TRAZ** — três degraus da mesma escada, não três
   * formulários. O que muda de um para o outro é de quem é a chrome em volta e,
   * como consequência, se existe ou não grade de meses embaixo da fileira.
   *
   * `'open'` (default) é o card do chat e a tela de busca do booking, INTACTOS:
   * casca de card, abas, fileira de campos, grade de meses INLINE embaixo e rodapé
   * com o CTA. No multi, o construtor de trechos expandido.
   *
   * `'band'` é a faixa de busca da bancada: o form é a fileira INTEIRA e nada mais —
   * abas em cima, campos numa linha e o CTA como última coluna dela, sem casca de
   * card e sem rodapé. **Sem grade embaixo**, e é essa a razão de existir do degrau:
   * a tela que hospeda a faixa é a LISTA de voos, e três meses inline põem a lista
   * fora do visível. Então o QUANDO deixa de ser espelho e vira CONTROLE (a mesma
   * `PlannerWhenField` do colapsado, com a grade no popover), que é o que o desenho
   * manda — quem passa `painelDeDatas` está dizendo "não tenho grade embaixo, põe a
   * sua aqui dentro" (`campos_da_busca.tsx`). No multi, a cadeia de trechos numa
   * linha (a mesma `PlannerLegsField`).
   *
   * `'collapsed'` é o degrau de baixo: o form como UM campo de uma chrome que já é
   * dona da rota, das abas e do gatilho de busca. Só o campo QUANDO (ou, no multi, a
   * linha de trechos), e o primário do popover é a ÚNICA saída — é ele que ecoa.
   * Sem host em produção hoje; a faixa da bancada é o degrau do meio, não este.
   *
   * O eixo "grade embaixo × grade no popover" NÃO é prop separada de propósito: o
   * colapsado não pode ter grade embaixo (não há corpo onde pô-la), então uma
   * segunda prop admitiria uma combinação impossível — e um host que quer a fileira
   * inteira sem grade embaixo pede UM valor, não dois.
   */
  layout?: 'open' | 'band' | 'collapsed';
  /**
   * O layout aberto SEM a casca de card (#2167): os campos moram soltos na página
   * e o CTA vira o "Buscar" pill-ink inline — fecha a fileira binária depois do
   * QUANDO; no multi mora na linha de status da lista. O rodapé de card morre
   * junto (a `note` não tem onde morar — o host desta variante não a passa).
   * É a tela de busca determinística (`CabecalhoDeBusca` do protótipo,
   * `contexto="pagina"`). MODIFICADOR aditivo do `'open'`, não um 4º degrau de
   * `layout`: a escada mede "quanta superfície o form traz", e aqui a superfície
   * é a mesma do card — só a moldura sai. band/collapsed ignoram. Default
   * `false`: o card do chat e todo host de hoje seguem intactos.
   */
  naked?: boolean;
  /**
   * O card do CHAT sem a SAIA (#2207): a casca de card fica — é a bolha do turno
   * —, o rodapé sai e o disparo vira a lupa only-icon dentro da fileira (binário)
   * ou da linha de status da lista (multi). A saia existia só pra hospedar o
   * `Buscar` em régua `fui-btn-lg`, e era ela que fazia o card medir mais alto do
   * que o conteúdo pede.
   *
   * MODIFICADOR aditivo do `'open'`, irmão de `naked` e ORTOGONAL a ele: `naked`
   * tira a CASCA (a tela determinística, que mantém lupa + rótulo), `inlineSearch`
   * tira a SAIA. Ligados juntos, `naked` vence — ele já não tem rodapé. band e
   * collapsed ignoram. Default `false`: os dois overlays da pill, que põem
   * `Cancelar` no rodapé, seguem intactos.
   *
   * `inlineSearch` descarta `onCancel` e `note`: sem rodapé, não há onde
   * pô-los — um host que ligasse os dois perderia o `Cancelar` sem erro de
   * tipo nem pista visual, porque o `return` do rodapé acontece antes até do
   * `const cancel` ser montado.
   */
  inlineSearch?: boolean;
  /** Busca remota de aeroportos, já em opções de combobox. PRECISA ser
   *  referencialmente estável (memoizada pelo host) — é dep do effect de
   *  busca do PlaceField; uma arrow inline re-dispararia a busca a cada render.
   *  Opcional pelo DEGRADE (ver `NO_AIRPORTS`), não porque algum layout dispense a
   *  busca de praça: a faixa do explorer é o layout ABERTO, com a fileira de rota
   *  inteira, e mesmo o colapsado tem combobox dentro do construtor de trechos. */
  searchAirports?: (term: string) => Promise<SearchSelectOption[]>;
  /**
   * Dispara um pedido de heat. Fire-and-forget: nada de retorno — a resposta
   * entra pela prop `heat` (chat: shell reconcilia o bloco; bancada: estado
   * local). `meta.trigger` distingue quem pediu — `'auto'` no self-heal do
   * mount (binário e por-perna do multi); AUSENTE nos gestos explícitos
   * (pick/swap/pager/edição de trecho), porque o host que converte pra action
   * (`FlightPlannerCard`) já assume `'user'` como default — omitir aqui, e não
   * passar `{ trigger: 'user' }` redundante, é o que mantém as chamadas
   * manuais indistinguíveis das de antes do seam existir.
   */
  fetchHeat: (req: PlannerHeatRequest, meta?: { trigger: 'user' | 'auto' }) => void;
  /** Liga os reads AUTOMÁTICOS (self-heal no mount + heat por perna do multi).
   *  O card passa `Boolean(plannerPort)` (host vivo); os reads EXPLÍCITOS
   *  (pick de rota/swap/pager) disparam sempre. Default true. */
  autoFetchHeat?: boolean;
  onSubmit: (request: PlannerSubmit) => void;
  /** Reporta quando os campos vivos divergem da última linha de base SUBMETIDA
   *  (ou da semente do mount). O host usa pra travar o Confirmar: a tela nunca
   *  mostra um roteiro que o confirm não veria. Só emite em MUDANÇA de valor. */
  onDirtyChange?: (dirty: boolean) => void;
  /** Saída segura do host que monta o form como EDIÇÃO da busca corrente (a
   *  expansão da pill sobre a bancada, #2129): o rodapé ganha "Cancelar" (ghost,
   *  a mesma receita do Redefinir) ao lado do CTA — descarta o rascunho sem
   *  submeter nada. Ausente (tela de busca, card do chat), nada muda. Só o
   *  layout `open` tem rodapé; band/collapsed não têm onde pô-lo e ignoram. */
  onCancel?: () => void;
  /** O campo QUEM sai do layout aberto (o band já o omite por conta própria):
   *  pro host cujo pedido não carrega roster nem sujeito de política — o
   *  planner da pill do explorer —, o campo seria um convite morto ("Selecionar
   *  viajantes") cujo eco a re-busca ignora. Aditiva: sem a prop, nada muda. */
  hideWho?: boolean;
  /** Monta o form com o popover de DATAS já aberto (#2136) — o host da pill
   *  passa quando o segmento clicado foi o de datas (#2129): o gesto que abriu
   *  o overlay era um pedido de calendário, e sem isto ele nasceria fechado a um
   *  clique de distância. No multi abre o popover da PRIMEIRA perna. Só faz
   *  sentido no layout aberto; os demais ignoram. */
  initialDatesOpen?: boolean;
  /**
   * O popover de DATAS sai por PORTAL pro `document.body` e vira `position:
   * fixed` (#2174), em vez do `.fpl-pop` de sempre — `position: absolute`
   * dentro do form. Existe porque os overlays da pill (`.bpx-panel` do
   * web-chat, `.fxo-panel` do kit) têm `max-height` + `overflow-y: auto`, e
   * dentro deles o popover nascia CORTADO no pé do painel em vez de flutuar
   * por cima — o mesmo defeito que a lista de praças resolve por
   * portal (o popover do `PlaceField`). Ver `PlannerWhenField`.
   *
   * Default `false` preserva TODO host de hoje sem esta prop (o card do chat,
   * a faixa da bancada, a tela de busca) — só os dois overlays da pill, que
   * são os únicos com o ancestral que clipa, ligam.
   */
  floatDatesPopover?: boolean;
  /**
   * O preenchimento GUIADO da página determinística (ver `useGuidedFill`): o
   * primeiro campo vazio pede a vez (o anel de pendente, o mesmo `.is-wanted`
   * do multi) e escolher uma praça abre o campo seguinte — De → Para → Quando
   * — em popover na fileira larga e em folha no palco estreito; com o pedido
   * completo o foco pousa no Buscar. Só a continuação de um gesto abre — a
   * inversão das pontas só move o anel —, salvo ao nascer, com o primeiro
   * campo pendente já aberto (ver `guidedOpen`). Só o layout
   * aberto e não congelado. O card do chat liga enquanto é o último da
   * conversa (`FlightPlannerCard.superseded`); a faixa da bancada não passa.
   * Default `false`.
   */
  guided?: boolean;
  /**
   * Com `guided`, onde o primeiro campo pendente já nasce aberto e focado —
   * ver `GuidedOpen`. A página omite (só na fileira larga); o card do chat
   * passa `always` (também na folha do palco estreito) ou `never` (o card
   * remontado do histórico, cujo gesto já foi gasto).
   */
  guidedOpen?: Parameters<typeof useGuidedFill>[0]['opens'];
  /**
   * A cidade de um código IATA — o host passa o `cityByAirport` do domain, a
   * mesma autoridade do rótulo do combobox. Só nomeia o destino no título da
   * folha de datas do palco estreito ("Ida para São Paulo"); ausente, ou sem a
   * cidade do código, o título lê o rótulo de display do destino. Entra por
   * prop porque o form não importa o domain (#4998).
   */
  cityOfAirport?: (iata: string) => string | null | undefined;
  /**
   * O rótulo da pílula DE/PARA para um IATA cru (`GRU Guarulhos`), quando o
   * bloco chega só com o código. O host passa a mesma convenção das opções do
   * combobox (`airportLabelOf`); ausente, a pílula mostra o código. Entra por
   * prop porque o form não importa o domain (#4998).
   */
  airportLabel?: (iata: string) => string;
  /**
   * A busca de viajantes do QUEM. A conversa e as páginas passam o port
   * `travelers` do `GenUiConfig`; ausente, o campo lista só quem já está no
   * pedido. Entra por prop porque o form não lê o `GenUiConfig` (#4998).
   */
  travelerDirectory?: PlannerTravelerDirectory;
  /**
   * O histórico de aeroportos do viajante: o combobox DE/PARA abre OFERECENDO
   * ("Recentes") em vez de pedir. O host o lê (`useRecentAirports`, com o
   * port dele) e passa; ausente ou vazio, o campo segue só com a busca por
   * texto. Entra por prop, e não pelo `GenUiConfig`, para a página não depender
   * do provider da conversa (#4998).
   */
  recentAirports?: PlannerRecentAirports;
  /** Os textos do form. O host os monta com `flightPlannerCopy(locale)` — a
   *  conversa com o locale do `GenUiConfig`, a página com o do app — e o form não
   *  lê o catálogo do genui-kit (#4998). */
  copy: FlightPlannerCopy;
  /** O locale das datas e moedas, o mesmo com que o `copy` foi montado. */
  locale: string;
};

export type PlannerBodyProps = {
  readonly state: PlannerState;
  readonly binary: BinaryPlanner;
  readonly multi: MultiPlanner;
  readonly band: boolean;
  readonly collapsed: boolean;
  /** Ver a doc em `PlannerFormProps` (#2167). `| undefined` explícito (não `?:`)
   *  pra viajar sem default nem spread — o form está no teto de complexidade. */
  readonly naked: boolean | undefined;
  /** Ver a doc em `PlannerFormProps` (#2207). `| undefined` explícito (não `?:`)
   *  pra viajar sem default nem spread, igual a `naked`. */
  readonly inlineSearch: boolean | undefined;
  readonly readOnly: boolean;
  readonly initial: PlannerFormProps['initial'];
  readonly searchAirports: NonNullable<PlannerFormProps['searchAirports']>;
  readonly originSections: ReturnType<typeof recentSection>;
  readonly destinationSections: ReturnType<typeof recentSection>;
  readonly ctaLabel: string;
  readonly confirmDatesLabel: string;
  readonly loading: boolean;
  readonly todayIso: string;
  readonly choice: string;
  readonly best?: SkirtBest;
  readonly note?: string;
  readonly whenLabel: string;
  /** A meia-resposta do ida e volta ("– Volta", #2136). `| undefined` explícito
   *  (não `?:`) pra viajar sem spread condicional — o form já está no teto de
   *  complexidade. */
  readonly whenPendingTail: string | undefined;
  /** O popover de datas do layout ABERTO, controlado pelo form (#2136): é o que
   *  deixa o pick do destino abri-lo sozinho. */
  readonly whenOpen: boolean;
  readonly onWhenOpenChange: (open: boolean) => void;
  readonly openDates: () => void;
  readonly restoreDates: () => void;
  readonly openLegs: () => void;
  readonly restoreLegs: () => void;
  readonly submitDates: () => void;
  readonly selectTripType: (type: PlannerTripType) => void;
  /** Ver `resetPlanner` — o Redefinir da saia do multi (#2362). */
  readonly onReset: () => void;
  readonly onCancel?: () => void;
  readonly hideWho: boolean;
  /** Ver `PlannerFormProps.floatDatesPopover` — repassado ao QUANDO binário
   *  (`BinaryWhenView`) e ao QUANDO por linha do multi (`LegWhenField`); os
   *  dois resolvem o default `false` (nenhum tem orçamento de complexidade
   *  livre em `PlannerForm`/`PlannerBody` pra fazer isso aqui). */
  readonly floatDatesPopover?: boolean;
  /** Palco estreito (`NARROW_STAGE_QUERY`): no layout aberto o QUANDO abre em
   *  folha de tela cheia em vez de popover (#2653). Resolvido em
   *  `BinaryWhenView`/`LegWhenField`, pelo mesmo orçamento de `floatDatesPopover`. */
  readonly narrow: boolean;
  /** O preenchimento guiado da página (`useGuidedFill`) — inerte fora dela. */
  readonly guide: PlannerGuide;
  /** Ver `PlannerFormProps.cityOfAirport`. `| undefined` explícito (não `?:`)
   *  pra viajar sem spread condicional, como `naked`. */
  readonly cityOfAirport: PlannerFormProps['cityOfAirport'] | undefined;
  /** Ver `PlannerFormProps.airportLabel`, com o mesmo `| undefined` explícito. */
  readonly airportLabel: PlannerFormProps['airportLabel'] | undefined;
  /** Ver `PlannerFormProps.travelerDirectory`, idem. */
  readonly travelerDirectory: PlannerFormProps['travelerDirectory'] | undefined;
  readonly copy: FlightPlannerCopy;
  readonly locale: string;
};
